import { getProjectDuration } from '@ors/components/manage/Blocks/ProjectsListing/utils'
import { formatNumberValue } from '@ors/components/manage/Blocks/Replenishment/utils'
import {
  cdField,
  pcrFieldsMapping,
  pcrFieldsErrorsMapping,
  summaryOfKeyDataField,
  requiredMessage,
} from './constants'
import {
  PCRData,
  PCRAlternativeTechnologyType,
  PCREnterpriseType,
  PCREquipmentType,
  PCRSummaryOfKeyDataType,
  PCRResultsAssessmentData,
  PCRCausesOfDelayData,
  PCRProjectRecommendationsData,
  PCRGenderMainstreamingData,
  PCRSupportingEvidencesData,
  FormattedSupportingEvidencesData,
  OptionsType,
} from './interfaces'
import { useGetPCRDefaults } from './hooks/useGetPCRDefaults'
import { ApiAgency } from '@ors/types/api_agencies'

import cx from 'classnames'
import dayjs from 'dayjs'
import {
  filter,
  find,
  findIndex,
  forEach,
  isObject,
  keys,
  lowerCase,
  map,
  omit,
  pick,
  reduce,
  some,
  sumBy,
} from 'lodash'

export type PCRSummaryProjectPayload = {
  project_id: number
  funds_disbursed?: string
  planned_date_of_completion?: string
  alternative_technologies?: PCRAlternativeTechnologyType[]
  enterprises?: PCREnterpriseType[]
  equipments?: PCREquipmentType[]
}

const hasText = (value: string) => value.trim() !== ''

const cleanNumber = (value: string) => {
  const cleanedValue = value.replace(/,/g, '').trim()
  return cleanedValue || undefined
}

const cleanDate = (value: string) => value.trim() || undefined

const isAlternativeTechnologySent = (entry: PCRAlternativeTechnologyType) =>
  entry.substance_from !== null || entry.substance_to !== null

const isEnterpriseSent = (entry: PCREnterpriseType) =>
  hasText(entry.name) || hasText(entry.address)

const isEquipmentSent = (entry: PCREquipmentType) =>
  hasText(entry.name) ||
  hasText(entry.description) ||
  entry.disposal_type !== null ||
  hasText(entry.disposal_date)

const cleanAlternativeTechnologies = (
  entries: PCRAlternativeTechnologyType[],
) => entries.filter(isAlternativeTechnologySent)

const cleanEnterprises = (entries: PCREnterpriseType[]) =>
  entries
    .map((entry) => ({
      name: entry.name.trim(),
      address: entry.address.trim(),
    }))
    .filter(isEnterpriseSent)

const cleanEquipments = (entries: PCREquipmentType[]) =>
  entries
    .map((entry) => ({
      name: entry.name.trim(),
      description: entry.description.trim(),
      disposal_type: entry.disposal_type,
      disposal_date: entry.disposal_date,
    }))
    .filter(isEquipmentSent)

export const buildPCRProjectPayload = (
  entry: PCRSummaryOfKeyDataType,
): PCRSummaryProjectPayload => {
  const payload: PCRSummaryProjectPayload = {
    project_id: entry.project_id,
  }
  const fundsDisbursed = cleanNumber(entry.funds_disbursed)
  const plannedDateOfCompletion = cleanDate(entry.planned_date_of_completion)
  const alternativeTechnologies = cleanAlternativeTechnologies(
    entry.alternative_technologies,
  )
  const enterprises = cleanEnterprises(entry.enterprises)
  const equipments = cleanEquipments(entry.equipments)

  if (fundsDisbursed) {
    payload.funds_disbursed = fundsDisbursed
  }
  if (plannedDateOfCompletion) {
    payload.planned_date_of_completion = plannedDateOfCompletion
  }
  if (alternativeTechnologies.length > 0) {
    payload.alternative_technologies = alternativeTechnologies
  }
  if (enterprises.length > 0) {
    payload.enterprises = enterprises
  }
  if (equipments.length > 0) {
    payload.equipments = equipments
  }

  return payload
}

const isProjectSent = (entry: PCRSummaryOfKeyDataType) =>
  keys(buildPCRProjectPayload(entry)).length > 1

const alignRowErrors = (
  rows: any[],
  isSent: (row: any) => boolean,
  rowErrors: unknown,
) => {
  if (!Array.isArray(rowErrors) || !rowErrors.every(isObject)) {
    return rowErrors
  }

  let sentIndex = 0

  return map(rows, (row) => (isSent(row) ? (rowErrors[sentIndex++] ?? {}) : {}))
}

export const alignSummaryOfKeyDataErrors = (
  errors: Record<string, any>,
  summaryOfKeyData: PCRSummaryOfKeyDataType[],
) => {
  const projectsErrors = errors?.[summaryOfKeyDataField]

  if (!Array.isArray(projectsErrors)) {
    return errors
  }

  let sentIndex = 0

  const alignedErrors = map(summaryOfKeyData, (entry) => {
    if (!isProjectSent(entry)) {
      return {}
    }

    const projectErrors = projectsErrors[sentIndex++] ?? {}

    return {
      ...projectErrors,
      ...(projectErrors.alternative_technologies && {
        alternative_technologies: alignRowErrors(
          entry.alternative_technologies,
          isAlternativeTechnologySent,
          projectErrors.alternative_technologies,
        ),
      }),
      ...(projectErrors.enterprises && {
        enterprises: alignRowErrors(
          entry.enterprises,
          isEnterpriseSent,
          projectErrors.enterprises,
        ),
      }),
      ...(projectErrors.equipments && {
        equipments: alignRowErrors(
          entry.equipments,
          isEquipmentSent,
          projectErrors.equipments,
        ),
      }),
    }
  })

  return { ...errors, [summaryOfKeyDataField]: alignedErrors }
}

export const getOtherOptionId = (options: OptionsType[]) =>
  find(options, (option) => lowerCase(option.name).includes('other'))?.id

export const formatOptions = (options: [string, string][]) =>
  map(options, (option) => ({
    id: option[0],
    name: option[1],
  }))

export const formatAgencyData = <T extends { agency_id: number }>(
  data: T[],
  subField: keyof T,
) =>
  reduce(
    data,
    (acc: Record<string, any>[], entry) => {
      forEach(entry[subField] as object[], (subEntry) => {
        acc.push({ agency_id: entry.agency_id, ...subEntry })
      })

      return acc
    },
    [],
  )

export const checkHasErrors = (entry: Record<string, any>) =>
  entry && Object.keys(entry).length > 0

export const hasSectionErrors = (errors: Record<string, any>) =>
  Object.values(errors).some((error) => {
    if (!Array.isArray(error)) {
      return Object.values(error as Record<string, any>).some((nestedError) =>
        nestedError.some((item: Record<string, any>) =>
          Array.isArray(item.errors)
            ? item.errors.some(checkHasErrors)
            : checkHasErrors(item.errors),
        ),
      )
    }

    return error.some((item) => {
      if (Array.isArray(item)) {
        return item.some(checkHasErrors)
      }

      return typeof item === 'string' || checkHasErrors(item)
    })
  })

const formatNestedErrors = (
  errors: Record<string, any>,
  crtFieldNames: Record<string, string>,
  field: string,
  index: number,
) => {
  const errorFields = Object.keys(errors)

  if (errorFields.length !== 0) {
    const fieldNames = map(errorFields, (field) => crtFieldNames[field]).join(
      ', ',
    )
    const errorMessage =
      errorFields.length > 1 ? 'These fields are' : 'This field is'

    return {
      id: `${field}-${index}`,
      message: `${crtFieldNames[field]} ${index + 1} : ${fieldNames} - ${errorMessage} not valid.`,
    }
  }

  return null
}

export const formatErrors = (
  errors: { [key: string]: string[] },
  nestedField?: string,
  tabKey?: string,
) => {
  const extraFieldNames: Record<string, string> = !!tabKey
    ? {
        name:
          {
            enterprises: 'Name of enterprise',
            equipments: 'Name of equipment',
          }[tabKey] ?? '',
      }
    : {}

  const initialFieldNames = { ...pcrFieldsMapping, ...pcrFieldsErrorsMapping }
  const fieldNames = !!tabKey
    ? { ...initialFieldNames, ...extraFieldNames }
    : initialFieldNames

  return Object.entries(errors)
    .filter(([, error]) => error.length > 0)
    .flatMap(([field, error]) =>
      error
        .flatMap((nestedError, index) => {
          if (typeof nestedError === 'string') {
            return {
              id: `${field}-${index}`,
              message: `${fieldNames[field]}: ${nestedError}`,
            }
          } else {
            if (Array.isArray(nestedError)) {
              return reduce(
                nestedError,
                (acc: any[], entry: Record<string, any>, nestedIndex) => [
                  ...acc,
                  formatNestedErrors(entry, fieldNames, field, nestedIndex),
                  ...map(entry[nestedField!], (nestedEntry, deepNestedIndex) =>
                    formatNestedErrors(
                      nestedEntry,
                      fieldNames,
                      nestedField!,
                      deepNestedIndex,
                    ),
                  ),
                ],
                [],
              )
            }

            if (Object.keys(nestedError).length !== 0) {
              return formatNestedErrors(nestedError, fieldNames, field, index)
            }

            return null
          }
        })
        .filter(Boolean),
    )
}

export const getErrorIndex = (
  sectionData: any,
  field: string,
  crtAgencyId: number,
  index: number,
) => {
  const countByAgency = map(sectionData, (data) => ({
    [data.agency_id]: data[field].length,
  }))

  const crtAgencyIndex = findIndex(
    countByAgency,
    (entry) => Object.keys(entry)[0] === String(crtAgencyId),
  )

  return (
    sumBy(
      countByAgency.slice(0, crtAgencyIndex),
      (entry) => Object.values(entry)[0],
    ) + index
  )
}

const hasErrorMessage = (errors: Record<string, any>): boolean => {
  if (typeof errors === 'string') {
    return errors === requiredMessage
  }

  if (Array.isArray(errors)) {
    return errors.some((error) => hasErrorMessage(error))
  }

  if (errors && typeof errors === 'object') {
    return Object.values(errors).some((error) => hasErrorMessage(error))
  }

  return false
}

export const getSectionAgencies = (agencies: ApiAgency[], sectionData: any) =>
  agencies && agencies.length > 0
    ? map(
        sectionData,
        (entry) => find(agencies, (agency) => agency.id === entry.agency_id)!,
      )
    : []

export const normalizeErrors = (errors: Record<string, any>) =>
  Object.fromEntries(
    Object.entries(errors).filter(([, value]) => {
      if (!Array.isArray(value)) {
        return true
      }

      return some(value, checkHasErrors)
    }),
  )

export const groupSummaryOfKeyDataErrors = (
  errors: Record<string, any>,
  projectId: number,
) => {
  const crtProjectErrors = errors[projectId]

  if (!crtProjectErrors) {
    return errors
  }

  const projectErrors = crtProjectErrors[0].errors
  const groupedErrors = {
    general: pick(projectErrors, [
      'funds_disbursed',
      'planned_date_of_completion',
    ]),
    alternative_technologies: pick(projectErrors, 'alternative_technologies'),
    enterprises: pick(projectErrors, 'enterprises'),
    equipments: pick(projectErrors, 'equipments'),
  }

  return groupedErrors
}

export const getFormData = (
  pcrDefaultData: ReturnType<typeof useGetPCRDefaults>,
  PCRData: PCRData,
  metaProjectId: number,
  ratingOptions: OptionsType[],
) => {
  const {
    overview,
    results_assessment,
    delay_causes,
    learned_lessons,
    future_project_recommendations,
    gender_mainstreaming,
    sdgs_contribution,
    supporting_evidences,
  } = PCRData

  const overviewPrefilledData = {
    ...pick(pcrDefaultData.data, [
      'project_date_approved',
      'project_date_completion',
      'phase_out_ods_actual',
      'phase_out_ods_approved',
      'phase_out_co2_eq_t_actual',
      'phase_out_co2_eq_t_approved',
    ]),
    decision_ids: pcrDefaultData.data?.decisions,
  }

  const overviewData = {
    ...overview,
    rating_explanation_other:
      overview.rating === getOtherOptionId(ratingOptions)
        ? overview.rating_explanation_other
        : null,
  }

  const summaryOfKeyData = PCRData.summary_of_key_data.map(
    buildPCRProjectPayload,
  )
  const filteredSummaryOfKeyData = filter(
    summaryOfKeyData,
    (entry) => keys(entry).length > 1,
  )

  const resultsAssessmentData = formatAgencyData<PCRResultsAssessmentData>(
    results_assessment,
    'overall_assessment_highlights',
  )

  // const causesOfDelayProjectComponents = flatMap(
  //   delay_causes,
  //   ({ agency_id, project_components }) =>
  //     map(
  //       project_components,
  //       ({ project_component_option_id, delay_causes }) => ({
  //         agency_id,
  //         project_component_option_id,
  //         delay_causes,
  //         learned_lessons: [],
  //       }),
  //     ),
  // )

  // const projectComponentsData = [...causesOfDelayProjectComponents]

  const causesOfDelayData = formatAgencyData<PCRCausesOfDelayData>(
    delay_causes,
    cdField,
  )

  const projectRecommendationsData =
    formatAgencyData<PCRProjectRecommendationsData>(
      future_project_recommendations,
      'recommendations',
    )

  const genderMainstreamingsData = formatAgencyData<PCRGenderMainstreamingData>(
    gender_mainstreaming,
    'gender_mainstreamings',
  )
  const sdgsContributionData = map(
    filter(
      sdgs_contribution,
      (sdg) => sdg.goals.length > 0 || !sdg.sdg_applying,
    ),
    (sdg) =>
      sdg.sdg_applying
        ? { ...sdg, sdg_not_applying_explanation: '' }
        : { ...sdg, goals: [] },
  )

  const formattedSupportingEvidence =
    formatAgencyData<PCRSupportingEvidencesData>(
      supporting_evidences,
      'evidences',
    ) as FormattedSupportingEvidencesData[]

  const supportingEvidencesData = map(formattedSupportingEvidence, (evidence) =>
    omit(evidence, 'file'),
  )

  const payload = {
    meta_project_id: metaProjectId,
    ...overviewPrefilledData,
    ...overviewData,
    pcr_projects: filteredSummaryOfKeyData,
    overall_assessment_highlights: resultsAssessmentData,
    // project_components: projectComponentsData,
    delay_causes: causesOfDelayData,
    learned_lessons,
    future_project_recommendations: projectRecommendationsData,
    gender_mainstreamings: genderMainstreamingsData,
    sustainable_development_goals: sdgsContributionData,
    supporting_evidences: supportingEvidencesData,
  }

  const formData = new FormData()
  formData.append('metadata', JSON.stringify(payload))
  formattedSupportingEvidence.forEach((evidence) => {
    formData.append('files', evidence.file)
  })

  return formData
}

export const isSubmitDisabled = (errors: Record<string, any>) => {
  const hasOverviewDefaultErrors = hasErrorMessage(errors.overview)
  const hasResultsAssessmentDefaultErrors = hasErrorMessage(
    errors.results_assessment,
  )
  const hasCausesOfDelayDefaultErrors = hasErrorMessage(errors.delay_causes)
  const hasLessonsLearnedDefaultErrors = hasErrorMessage(errors.learned_lessons)
  const hasGenderMainstreamingDefaultErrors = hasErrorMessage(
    errors.gender_mainstreaming,
  )
  const hasSDGsDefaultErrors = hasErrorMessage(errors.sdgs_contribution)
  const hasEvidencesDefaultErrors = hasErrorMessage(errors.supporting_evidences)

  return (
    hasOverviewDefaultErrors ||
    hasResultsAssessmentDefaultErrors ||
    hasCausesOfDelayDefaultErrors ||
    hasLessonsLearnedDefaultErrors ||
    hasGenderMainstreamingDefaultErrors ||
    hasSDGsDefaultErrors ||
    hasEvidencesDefaultErrors
  )
}
export const formatDate = (date: string | null | undefined) =>
  date ? dayjs(date).format('DD/MM/YYYY') : ''

export const formatNumberProjectValue = (
  value: null | number | string | undefined,
  minDigits?: number,
  maxDigits?: number,
) => formatNumberValue(value ?? null, minDigits, maxDigits) ?? ''

export const getComputedFields = (
  crtProject: Record<string, any> | undefined,
  draftSummaryData: PCRSummaryOfKeyDataType | null,
) => {
  if (!crtProject) {
    return {
      planned_duration: 0,
      actual_duration: 0,
      delay: 0,
    }
  }

  const dateApproved = crtProject.date_approved ?? null
  const actualCompletionDate = crtProject.actual_date_of_completion ?? null
  const plannedCompletionDate =
    draftSummaryData?.planned_date_of_completion ?? null

  const plannedDuration =
    getProjectDuration({
      project_start_date: dateApproved,
      project_end_date: plannedCompletionDate,
    }) ?? 0
  const actualDuration =
    getProjectDuration({
      project_start_date: dateApproved,
      project_end_date: actualCompletionDate,
    }) ?? 0

  return {
    planned_duration: Number(plannedDuration),
    actual_duration: Number(actualDuration),
    delay: Number(actualDuration) - Number(plannedDuration),
  }
}

export const getFundingClassname = (index: number, isTotal: boolean) =>
  cx(
    'rounded-lg border-0 border-b-[3px] border-solid border-[#e5e7eb] bg-white p-4',
    {
      'shadow-[-3px_0_4px_-1px_#00000022,3px_0_4px_-1px_#00000022]':
        index === 0,
      'shadow-[-3px_0_12px_-1px_#00000022,3px_0_12px_-1px_#00000022]':
        index !== 0 && !isTotal,
      '!bg-[#F5FF8033] shadow-[-3px_0_7px_-1px_#00000015,3px_0_7px_-1px_#00000015,0_8px_24px_-2px_#00000044]':
        isTotal,
    },
  )

export const getValueClassName = (value: string) => ({
  '!bg-[#e0f0e6] !text-[#0c4b27] !border-[#b3dec5]': value === 'Yes',
  '!bg-[#fbe5e4] !text-[#8c2626] !border-[#f0cdcc]': value === 'No',
  '!bg-[#edf1f4] !text-[#424d54] !border-[#d9dde0]': value === 'N/A',
})
